import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import {
  GlobalAgentContext,
  type PinnedReference,
} from "@redotech/redo-merchant-app-common/global-agent-context";
import classNames from "classnames";
import { memo, useCallback, useContext } from "react";
import * as styles from "./ask-redo-pinned-references-row.module.css";

/**
 * Renders the row of pinned references the merchant has attached to the
 * Ask Redo chat (e.g. a Concierge message, an order, a return).
 *
 * Two modes:
 *  - **Live row** (no props): reads `pinnedReferences` from
 *    `GlobalAgentContext` and renders each as a removable chip. Used
 *    above the prompt input so the merchant sees what's currently
 *    attached and can drop it.
 *  - **Historical row** (`pins` prop): renders the supplied (frozen)
 *    list of references with no × button. Used above each user
 *    message in the chat history to surface what was attached at the
 *    moment of send. Read-only because removing a pin from a sent
 *    message doesn't unsend it.
 *
 * Adding a new pin-able thing (order, return, customer, etc.) needs no
 * changes here: provide a `label` + optional `Icon` on the pin and a
 * chip will appear automatically.
 */
export const AskRedoPinnedReferencesRow = memo(
  function AskRedoPinnedReferencesRow({
    pins,
    variant = "live",
  }: {
    /**
     * Frozen list of pins to render. When omitted, the row reads from
     * `pinnedReferences` on `GlobalAgentContext`.
     */
    pins?: readonly PinnedReference[];
    /**
     * `live` — chips have an × that calls `unpin` on the context.
     * `historical` — chips are read-only (no × button). Defaults to
     * `live`.
     */
    variant?: "live" | "historical";
  } = {}) {
    const { pinnedReferences, unpin } = useContext(GlobalAgentContext);
    const effectivePins = pins ?? pinnedReferences;

    if (effectivePins.length === 0) {
      return null;
    }

    const isHistorical = variant === "historical";

    return (
      <div
        className={classNames(styles.row, isHistorical && styles.rowHistorical)}
      >
        {effectivePins.map((ref) => (
          <PinnedReferenceChip
            key={ref.id}
            onRemove={isHistorical ? undefined : unpin}
            reference={ref}
            // Only the historical chip uses the two-line "card" layout
            // (icon + kind header above the snippet). The live chip
            // sitting above the prompt input stays a compact one-liner
            // so it doesn't double the chrome the merchant sees while
            // typing.
            showKind={isHistorical}
          />
        ))}
      </div>
    );
  },
);

const PinnedReferenceChip = memo(function PinnedReferenceChip({
  reference,
  onRemove,
  showKind = false,
}: {
  reference: {
    id: string;
    kind?: string;
    label: string;
    Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  };
  /** Omit to render the chip without a remove button (historical). */
  onRemove?: (id: string) => void;
  /**
   * Render the `kind` as a header line above the snippet (two-line
   * card layout). Off by default so the live row above the prompt
   * input stays compact.
   */
  showKind?: boolean;
}) {
  const handleRemove = useCallback(() => {
    onRemove?.(reference.id);
  }, [onRemove, reference.id]);

  const Icon = reference.Icon;
  const hasKind = showKind && Boolean(reference.kind);
  return (
    <div className={classNames(styles.chip, hasKind && styles.chipWithKind)}>
      {hasKind ? (
        // Two-line layout: small icon + kind label on the header row,
        // the actual snippet underneath. Tells the merchant at a glance
        // what type of thing was attached without leaving them to
        // decode the icon.
        <div className={styles.chipBody}>
          <div className={styles.chipHeader}>
            {Icon && (
              <span aria-hidden="true" className={styles.icon}>
                <Icon />
              </span>
            )}
            <span className={styles.kind}>{reference.kind}</span>
          </div>
          <span className={styles.text} title={reference.label}>
            {reference.label}
          </span>
        </div>
      ) : (
        <>
          {Icon && (
            <span aria-hidden="true" className={styles.icon}>
              <Icon />
            </span>
          )}
          <span className={styles.text} title={reference.label}>
            {reference.label}
          </span>
        </>
      )}
      {onRemove && (
        <button
          aria-label={`Remove ${reference.label}`}
          className={styles.remove}
          // Prevent the chip's parent `.ai-glow-focus` wrapper from
          // flashing its :focus-within glow when the merchant clicks ×.
          // `preventDefault` on mousedown stops the button from becoming
          // the focused element; the click handler still fires normally,
          // so the pin is removed without focus ever leaving its prior
          // home (typically <body> or wherever the merchant was).
          onClick={handleRemove}
          onMouseDown={(e) => e.preventDefault()}
          type="button"
        >
          <XCloseIcon aria-hidden="true" className={styles.removeIcon} />
        </button>
      )}
    </div>
  );
});
